import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

const MultiDatePicker = ({ selectedDates, onDatesChange, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Disable body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const toggleDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (selectedDates.includes(dateStr)) {
      // Remove date
      onDatesChange(selectedDates.filter(d => d !== dateStr));
    } else {
      // Add date
      onDatesChange([...selectedDates, dateStr]);
    }
  };

  const isDateSelected = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return selectedDates.includes(dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  const isPast = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(year, month, day);
    return checkDate < today;
  };

  const monthNames = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 
                      'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
  const dayNames = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
        overflowY: 'auto'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          // Click on backdrop closes modal
        }
      }}
    >
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        margin: 'auto'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>
            Vælg Datoer
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
            Klik på datoer for at vælge/fravælge. Klik OK når du er færdig.
          </p>
        </div>

        {/* Calendar Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <button
            type="button"
            onClick={previousMonth}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronLeft size={24} />
          </button>
          
          <h4 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
            {monthNames[month]} {year}
          </h4>
          
          <button
            type="button"
            onClick={nextMonth}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Day names */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0.25rem',
          marginBottom: '0.5rem'
        }}>
          {dayNames.map((day) => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#666',
                padding: '0.5rem 0'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0.25rem',
          marginBottom: '1.5rem'
        }}>
          {/* Empty cells for days before month starts */}
          {Array.from({ length: startingDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} style={{ padding: '0.75rem' }} />
          ))}
          
          {/* Days of the month */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const selected = isDateSelected(day);
            const past = isPast(day);
            const today = isToday(day);

            return (
              <button
                key={day}
                type="button"
                onClick={() => !past && toggleDate(day)}
                disabled={past}
                style={{
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: selected ? '#4caf50' : past ? '#f5f5f5' : today ? '#e3f2fd' : 'white',
                  color: selected ? 'white' : past ? '#ccc' : today ? '#2196f3' : '#333',
                  cursor: past ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: selected || today ? 600 : 400,
                  transition: 'all 0.2s',
                  position: 'relative',
                  border: today && !selected ? '2px solid #2196f3' : '2px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (!past && !selected) {
                    e.target.style.background = '#e8f5e9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!past && !selected) {
                    e.target.style.background = today ? '#e3f2fd' : 'white';
                  }
                }}
              >
                {day}
                {selected && (
                  <Check
                    size={14}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px'
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Selected dates summary */}
        {selectedDates.length > 0 && (
          <div style={{
            background: '#e8f5e9',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            maxHeight: '150px',
            overflowY: 'auto'
          }}>
            <strong style={{ fontSize: '0.95rem' }}>
              Valgte datoer ({selectedDates.length}):
            </strong>
            <div style={{ 
              marginTop: '0.5rem', 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '0.5rem' 
            }}>
              {selectedDates.sort().map(date => (
                <span
                  key={date}
                  style={{
                    background: '#4caf50',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}
                >
                  {new Date(date + 'T00:00:00').toLocaleDateString('da-DK', {
                    day: 'numeric',
                    month: 'short'
                  })}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: '#e5e5e5',
              color: '#333',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            Annuller
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={selectedDates.length === 0}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: selectedDates.length === 0 ? '#ccc' : '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedDates.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            OK ({selectedDates.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiDatePicker;
